import React, { useState } from 'react';
import moment from 'moment';
import './bankAccount.css';
import { capitalizeFirstLetter } from '../../../../shared/utils/function';
import { formatWalletAddress } from '../../../../shared/utils/transactionUtil';
import { ATTRIBUTE_LABELS, CRYPTO_CURRENCY_ICON } from '../../../../shared/constants/EarningsPayout';
import ChangePayoutMethodModal from '../../../Modals/ChangePayoutMethodModal';    

const CRYPTO_CURRENCIES = ['BTC', 'ETH', 'LTC', 'USDC', 'USDT'];

/**
 * Maps known payer_name keys to display values. If key matches, returns the mapped value;
 * otherwise returns the original payer name from props.
 * @param {string} payerName - Raw payer name (e.g. "ACH Next day")
 * @param {string} formattedAccountNumber - Last 4 masked, e.g. "****4991"
 * @returns {string} Display string (e.g. "ACH ****4991" or full payer name)
 */
const getPayerDisplayName = (payerName, formattedAccountNumber) => {
    const PAYER_DISPLAY_MAP = {
        'ACH Next day': (formatted) => (formatted ? `ACH ${formatted}` : 'ACH'),
    };
    const mapper = PAYER_DISPLAY_MAP[payerName];
    if (mapper) {
        return typeof mapper === 'function' ? mapper(formattedAccountNumber) : mapper;
    } else {
        return payerName + (formattedAccountNumber ? ` ${formattedAccountNumber}` : '');
    }
};



const PayoutMethodDisplay = ({ paymentMethod, userEmail }) => {
    const [isChangeModalOpen, setIsChangeModalOpen] = useState(false);

    const handleEditClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsChangeModalOpen(true);
    };

    const currencySymbol =
        paymentMethod?.exchange_rate?.[0]?.currency_symbol ||
        paymentMethod?.attributes?.[0]?.destination_currency ||
        '';

    const attributes = paymentMethod?.attributes?.[0]?.attributes || [];

    const getAttributeValue = (label) => attributes.find((attr) => attr.label === label)?.value || '';

    const payerName = paymentMethod?.payer_name || '';
    const isWallet = CRYPTO_CURRENCIES.includes(currencySymbol);

    const bankAccountNumber = getAttributeValue(ATTRIBUTE_LABELS.BANK_ACCOUNT_NUMBER);
    const formattedAccountNumber =
        bankAccountNumber && bankAccountNumber.length >= 4
            ? `****${bankAccountNumber.slice(-4)}`
            : bankAccountNumber || '';

    const routingNumber = getAttributeValue(ATTRIBUTE_LABELS.BANK_ROUTING_NUMBER);
    const accountType = getAttributeValue(ATTRIBUTE_LABELS.BANK_ACCOUNT_TYPE);

    const walletAccountNumber = attributes?.[0]?.value || '';
    const walletAddress = formatWalletAddress(walletAccountNumber, false);

    const createdDate = paymentMethod?.updated_at ? moment(paymentMethod.updated_at).format('MMM DD, YYYY') : '';

    const displayName = isWallet
        ? payerName
        : getPayerDisplayName(payerName, formattedAccountNumber);

    return (
        <>
            <div className="cm_payout_method_selected d-block">
                <div className="d-flex justify-content-between align-items-center">
                    <div>
                        <p className="mb-0 d-flex align-items-center">
                            <i
                                className={`mr-1 ${isWallet ? CRYPTO_CURRENCY_ICON[currencySymbol] : 'fa-solid fa-building-columns'
                                    }`}
                                aria-hidden="true"
                            />
                            <b>{displayName}</b>
                        </p>
                    </div>

                    <div className="d-flex align-items-center gap-2">
                        <span className="account_bage">Active</span>
                        <button
                            type="button"
                            className="btn-link payout-method-edit-button"
                            onClick={handleEditClick}
                            title="Edit payout method"
                            aria-label="Edit payout method"
                        >
                            <i className="fa-solid fa-pen-to-square payout-method-edit-icon" />
                        </button>
                    </div>
                </div>

                <p className="secondary-text">
                    {isWallet ? (
                        walletAddress
                    ) : (
                        <>
                            {routingNumber}
                            {accountType && (
                                <>
                                    {routingNumber && ' • '}
                                    {capitalizeFirstLetter(accountType)} Account
                                </>
                            )}
                        </>
                    )}
                </p>

                {createdDate && <small className="secondary-text">Added on {createdDate}</small>}
            </div>

            <ChangePayoutMethodModal
                isOpen={isChangeModalOpen}
                toggle={() => setIsChangeModalOpen(false)}
                userEmail={userEmail}
            />
        </>
    );
};

export default PayoutMethodDisplay;